import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Check, Lock } from 'lucide-react';
import { Input } from "../ui/input";
import { Button } from "../ui/button";

const CheckoutModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) return;
    
    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    setIsSuccess(true);
  };

  const handleClose = () => {
    setFormData({ name: '', email: '' });
    setIsSuccess(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={handleClose}
          data-testid="checkout-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="checkout-modal"
          >
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-2 text-[#4B5E53] hover:text-[#1A2E24] transition-colors z-10"
              data-testid="checkout-modal-close"
            >
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>

            {!isSuccess ? (
              <>
                {/* Header */}
                <div className="bg-[#2E5C41] px-6 py-8 text-center">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-full mb-4">
                    <Sparkles className="w-4 h-4 text-white" strokeWidth={1.5} />
                    <span className="text-xs font-medium text-white">Acceso Fundadores</span>
                  </div>
                  <h3 className="font-cabinet text-2xl font-bold text-white mb-2">
                    Reserva tu cupo
                  </h3>
                  <p className="text-white/80 text-sm">
                    Precio especial de fundador: <span className="font-bold">$29/mes</span>
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1A2E24] mb-1.5">
                      Tu nombre
                    </label>
                    <Input
                      type="text"
                      placeholder="Dr. María García"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="h-12 bg-[#FAFAF9] border-[#2E5C41]/20 focus:border-[#2E5C41] rounded-xl"
                      data-testid="checkout-name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A2E24] mb-1.5">
                      Tu email
                    </label>
                    <Input
                      type="email"
                      placeholder="maria@clinica.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="h-12 bg-[#FAFAF9] border-[#2E5C41]/20 focus:border-[#2E5C41] rounded-xl"
                      data-testid="checkout-email"
                      required
                    />
                  </div>

                  {/* Summary */}
                  <div className="p-4 bg-[#F2F4F0] rounded-xl space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#4B5E53]">Plan Fundador</span>
                      <span className="font-medium text-[#1A2E24]">$29/mes</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#4B5E53]">Precio regular</span>
                      <span className="line-through text-[#4B5E53]">$49/mes</span>
                    </div>
                    <div className="pt-2 border-t border-[#2E5C41]/10 flex justify-between">
                      <span className="font-medium text-[#1A2E24]">Tu ahorro</span>
                      <span className="font-bold text-[#2E5C41]">$20/mes</span>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-12 bg-[#2E5C41] hover:bg-[#1A2E24] text-white font-bold rounded-full"
                    data-testid="checkout-submit"
                  >
                    {isSubmitting ? (
                      <motion.span
                        animate={{ opacity: [0.5, 1, 0.5] }}
                        transition={{ repeat: Infinity, duration: 1.5 }}
                      >
                        Procesando...
                      </motion.span>
                    ) : (
                      'Continuar al pago seguro'
                    )}
                  </Button>

                  <div className="flex items-center justify-center gap-2 text-xs text-[#4B5E53]">
                    <Lock className="w-3 h-3" strokeWidth={1.5} />
                    <span>Pago seguro con Stripe • 30 días de garantía</span>
                  </div>
                </form>
              </>
            ) : (
              /* Success State */
              <div className="p-8 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", duration: 0.5 }}
                  className="w-16 h-16 rounded-full bg-[#2E5C41] flex items-center justify-center mx-auto mb-6"
                >
                  <Check className="w-8 h-8 text-white" strokeWidth={2} />
                </motion.div>
                <h3 className="font-cabinet text-2xl font-bold text-[#1A2E24] mb-2">
                  ¡Bienvenido a HabitIA!
                </h3>
                <p className="text-[#4B5E53] mb-6">
                  Te hemos enviado un email a <strong>{formData.email || 'tu correo'}</strong> con los siguientes pasos para configurar tu asistente IA.
                </p>
                <Button
                  onClick={handleClose}
                  className="px-8 py-3 bg-[#2E5C41] hover:bg-[#1A2E24] text-white font-medium rounded-full"
                  data-testid="checkout-success-close"
                >
                  Entendido
                </Button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CheckoutModal;
